import { useEffect, useRef } from "react";
import { Slide } from "@mui/material";
import * as echarts from "echarts";
import { VeryBtnTransparent } from "../../common/v2/VeryBtnTransparent";
import { classVeryBtnTransparent } from "../../common/v2/VeryBtnTransparent/classVeryBtnTransparent";
import { VeryBtnFill } from "../../common/v2/VeryBtnFill";
import { classVeryBtnFill } from "../../common/v2/VeryBtnFill/classVeryBtnFill";
import { classVeryIconGoogle } from "../../common/v2/VeryIconGoogle/classVeryIconGoogle";
import { VeryProfileImg } from "../../common/v2/VeryProfileImg";
import { getImageURL } from "../../../utils/handler_files";
import {
  getFormattedNumber,
  getNotNegativeNumber,
} from "../../../utils/handler_validations";
import { getDateFull_S1, momentES } from "../../../utils/handler_dates";
import { useCancellationsContext } from "../../../context";
import Styles from "./styles";

const CANCEL_LABELS = { 1: "Pasajero", 2: "Conductor" };

const MiniChart = ({ data = [], color = "#ef4444" }) => {
  const refDiv = useRef();
  const chartRef = useRef();

  useEffect(() => {
    if (!refDiv.current) return;
    if (!chartRef.current) {
      chartRef.current = echarts.init(refDiv.current);
    }
    const labels = data.map((d) => momentES(d.day).format("DD/MM"));
    const values = data.map((d) => getNotNegativeNumber(d.count));
    chartRef.current.setOption({
      grid: { top: 5, bottom: 18, left: 25, right: 5 },
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: { fontSize: 10, color: "#aaa" },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        axisLabel: { fontSize: 10, color: "#aaa" },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      series: [
        {
          type: "bar",
          data: values,
          itemStyle: { borderRadius: [3, 3, 0, 0], color },
          barMaxWidth: 20,
        },
      ],
      tooltip: {
        trigger: "axis",
        backgroundColor: "#13131d",
        borderWidth: 0,
        textStyle: { color: "#fff", fontSize: 12, fontFamily: "Roboto" },
      },
    });
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [data, color]);

  return <div className="div-chart" ref={refDiv} />;
};

const STATUS_LABELS = {
  PENDING: "Pendiente",
  IN_PROGRESS: "En proceso",
  COMPLETED: "Completado",
};

const UserCard = ({
  user,
  isDriver,
  onBlock,
  onCompensate,
  actionLoading,
  tripStatus,
  driverBlocked,
  passengerCompensated,
}) => {
  if (!user) return null;
  const profileImg = getImageURL(
    process.env.REACT_APP_BUCKET,
    user.profileImg,
    80,
    80,
  );
  const cancelRate =
    user.cancelsByOwn + user.completedTrips > 0
      ? (
          (user.cancelsByOwn / (user.cancelsByOwn + user.completedTrips)) *
          100
        ).toFixed(1)
      : "0.0";

  return (
    <div className="div-userCard">
      <div className="div-userHeader">
        <VeryProfileImg CSS={[]} sizeIcon={28} sizeImg={40} src={profileImg} />
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <span className="txt-name">
            {user.name} {user.lastName}
          </span>
          <span className="txt-tel">{user.tel}</span>
        </div>
      </div>

      <div className="div-stats">
        <div className="stat-item">
          <span className="stat-val">
            {getFormattedNumber(user.cancelsByOwn, true, 0)}
          </span>
          <span className="stat-lbl">Cancelaciones propias</span>
        </div>
        <div className="stat-item">
          <span className="stat-val">
            {getFormattedNumber(user.completedTrips, true, 0)}
          </span>
          <span className="stat-lbl">Viajes completados</span>
        </div>
        <div className="stat-item">
          <span className="stat-val">{cancelRate}%</span>
          <span className="stat-lbl">Tasa cancel.</span>
        </div>
        <div className="stat-item">
          <span className="stat-val">
            {getFormattedNumber(user.count30d, true, 0)}
          </span>
          <span className="stat-lbl">Últimos 30d</span>
        </div>
      </div>

      {user.chart7d?.length > 0 && (
        <>
          <span className="txt-lastTitle">Cancelaciones últimos 7 días</span>
          <MiniChart
            data={user.chart7d}
            color={isDriver ? "#f59e0b" : "#3b82f6"}
          />
        </>
      )}

      {user.lastMotives?.length > 0 && (
        <>
          <span className="txt-lastTitle">Últimos motivos de cancelación</span>
          <div className="div-lastMotives">
            {user.lastMotives.map((m, i) => (
              <span key={i} className="motive-row">
                {m.motive || "Sin motivo"}{" "}
                <span style={{ color: "#aaa", fontSize: 11 }}>
                  — {m.date ? getDateFull_S1(momentES(m.date)) : ""}
                </span>
              </span>
            ))}
          </div>
        </>
      )}

      {isDriver && user.activeBlock && (
        <div className="div-blockBadge">
          <span className="lbl-block">
            Bloqueado hasta:{" "}
            {getDateFull_S1(momentES(user.activeBlock.blockUntil))}
            {user.activeBlock.reason ? ` — ${user.activeBlock.reason}` : ""}
          </span>
        </div>
      )}

      {!isDriver && user.compensation && (
        <div className="div-compensateBadge">
          <span className="lbl-compensate">
            ✓ Compensado con $
            {getFormattedNumber(user.compensation.amount, true, 0)}
            {user.compensation.couponCode
              ? ` (${user.compensation.couponCode})`
              : ""}
          </span>
        </div>
      )}

      <div className="div-actions">
        {isDriver && (
          <VeryBtnFill
            CSS={["btn-action"]}
            label={driverBlocked ? "Conductor bloqueado" : "Bloquear conductor"}
            colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
            isLoading={actionLoading}
            isDisabled={
              actionLoading || driverBlocked || tripStatus === "COMPLETED"
            }
            onClick={onBlock}
          />
        )}
        {!isDriver && (
          <VeryBtnFill
            CSS={["btn-action"]}
            label={
              passengerCompensated
                ? "Pasajero compensado"
                : "Compensar con cupón"
            }
            colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
            isLoading={actionLoading && !passengerCompensated}
            isDisabled={
              actionLoading ||
              passengerCompensated ||
              tripStatus === "COMPLETED"
            }
            onClick={!passengerCompensated ? onCompensate : undefined}
          />
        )}
      </div>
    </div>
  );
};

export const CancellationDetailPanel = ({ refSlide }) => {
  const cancellations = useCancellationsContext();
  const {
    detail,
    detailLoading,
    detailError,
    selectedServiceId,
    actionLoading,
  } = cancellations;

  const renderContent = () => {
    if (detailLoading) {
      return (
        <div className="div-loading">
          <span style={{ fontSize: 13, color: "#aaa", fontFamily: "Roboto" }}>
            Cargando...
          </span>
        </div>
      );
    }
    if (detailError) {
      return (
        <div className="div-error">
          <span className="txt-error">{detailError}</span>
        </div>
      );
    }
    if (!detail) return null;

    const { trip, passenger, driver } = detail;

    const isCompleted = trip.status === "COMPLETED";

    return (
      <>
        <div className="div-section">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span className="txt-sectionTitle">
              Cancelación #{trip.serviceId}
            </span>
            <span
              className={`tag-status ${isCompleted ? "tag-both" : trip.status === "IN_PROGRESS" ? "tag-compensated" : "tag-pending"}`}
              style={{
                borderRadius: 20,
                padding: "3px 12px",
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "Roboto",
              }}
            >
              {STATUS_LABELS[trip.status] || trip.status}
            </span>
          </div>
          <div className="div-tripCard">
            <div className="row">
              <span className="lbl-key">Cancelado por</span>
              <span
                className={`tag-canceledBy ${trip.canceledBy === 2 ? "driver" : "passenger"}`}
              >
                {CANCEL_LABELS[trip.canceledBy] || "—"}
              </span>
            </div>
            <div className="row">
              <span className="lbl-key">Motivo</span>
              <span className="lbl-val">{trip.motive || "Sin motivo"}</span>
            </div>
            <div className="row">
              <span className="lbl-key">Fecha</span>
              <span className="lbl-val">
                {trip.canceledDate
                  ? getDateFull_S1(momentES(trip.canceledDate))
                  : "—"}
              </span>
            </div>
            <div className="row">
              <span className="lbl-key">Costo</span>
              <span className="lbl-val">
                ${getFormattedNumber(trip.cost, true, 2)} MXN
              </span>
            </div>
            <div className="row">
              <span className="lbl-key">Distancia</span>
              <span className="lbl-val">
                {getFormattedNumber(trip.distance / 1000, true, 1)} km
              </span>
            </div>
          </div>
        </div>

        {passenger && (
          <div className="div-section">
            <span className="txt-sectionTitle">Pasajero</span>
            <UserCard
              user={passenger}
              isDriver={false}
              actionLoading={actionLoading}
              tripStatus={trip.status}
              passengerCompensated={trip.passengerCompensated}
              onCompensate={() =>
                cancellations.showModalCompensatePassenger(
                  passenger.id,
                  `${passenger.name} ${passenger.lastName}`,
                )
              }
            />
          </div>
        )}

        {driver && (
          <div className="div-section">
            <span className="txt-sectionTitle">Conductor</span>
            <UserCard
              user={driver}
              isDriver={true}
              actionLoading={actionLoading}
              tripStatus={trip.status}
              driverBlocked={trip.driverBlocked}
              onBlock={() =>
                cancellations.showModalBlockDriver(
                  driver.id,
                  `${driver.name} ${driver.lastName}`,
                )
              }
            />
          </div>
        )}

        <div className="div-section" style={{ marginTop: 8 }}>
          <VeryBtnFill
            CSS={["btn-action"]}
            label={isCompleted ? "Caso completado" : "Marcar como completado"}
            colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
            isLoading={actionLoading}
            isDisabled={actionLoading || isCompleted}
            onClick={!isCompleted ? cancellations.execMarkCompleted : undefined}
          />
        </div>
      </>
    );
  };

  return (
    <Slide
      direction="left"
      in={selectedServiceId > 0}
      timeout={400}
      container={refSlide?.current}
      unmountOnExit
    >
      <Styles.DivMain>
        <div className="div-header">
          <label className="txt-title">Detalle de cancelación</label>
          <VeryBtnTransparent
            CSS={["btnClose"]}
            colorTheme={classVeryBtnTransparent.COLOR_THEME.BLACK_S2}
            icon={{
              name: "close",
              type: classVeryIconGoogle.ICON_TYPE.SYMBOLS_OUTLINED,
              size: 22,
            }}
            onClick={() => cancellations.closeDetail()}
          />
        </div>
        {renderContent()}
      </Styles.DivMain>
    </Slide>
  );
};
